import React, { useState, useEffect } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Dimensions,
  ActivityIndicator,
  Modal
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons, FontAwesome5 } from '@expo/vector-icons';
import { API_BASE_URL } from '../config/api';
import Constants from "expo-constants";
import TurnstileWidget from '../components/TurnstileWidget';
import '../styles/custom.css';
import { useFonts } from 'expo-font';
import { MaskedTextInput } from "react-native-mask-text";
import { useCustomAlertModal } from '../components/CustomAlertModal';
import { useBannerWithPopUp } from '../components/BannerWithPopUp';

const { width, height } = Dimensions.get('window');

export default function PhoneAuthScreen({ navigation }) {
  const { 
    customAlertModal, 
    showCustomAlert, 
    hideCustomAlert, 
    handleCustomAlertConfirm
  } = useCustomAlertModal();
  const { 
    bannerWrapper
  } = useBannerWithPopUp( { marginTop: 16 } );
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [googleButtonClicked, setGoogleButtonClicked] = useState(false);
  const [facebookButtonClicked, setFacebookButtonClicked] = useState(false);
  const [appleButtonClicked, setAppleButtonClicked] = useState(false);

  const [captchaToken, setCaptchaToken] = useState(null);

  const validateForm = () => {
    console.log('=== FORM VALIDATION START ===');

    // Validate that phone is provided
    if (!phone.trim()) {
      console.log('VALIDATION FAILED: No phone provided');
      showCustomAlert('Error', 'Nomor handphone harus diisi');
      return false;
    }

    // Validate phone format if phone is provided
    if (phone.trim()) {
      // Basic phone validation - should contain only numbers and common phone characters
      const phoneRegex = /^[\d\s\-\+\(\)]+$/;
      if (!phoneRegex.test(phone)) {
        console.log('VALIDATION FAILED: Invalid phone format:', phone);
        showCustomAlert('Error', 'Format nomor handphone tidak valid');
        return false;
      }
    }

    // Validate phone format to make sure it is valid Indonesian cellular number without heading of 08/628/+628
    if (phone.length < 8 || phone.length > 13) {
      console.log('VALIDATION FAILED: Invalid phone format:', phone);
      showCustomAlert('Error', 'Format nomor handphone tidak valid');
      return false;
    }

    // Validate phone format to make sure it is valid Indonesian cellular number without heading of 08/628/+628
    if (phone.startsWith('08') || phone.startsWith('628') || phone.startsWith('+628')) {
      console.log('VALIDATION FAILED: Invalid phone format:', phone);
      showCustomAlert('Error', 'Ketikkan nomor handphone setelah "08" saja');
      return false;
    }

    console.log('=== FORM VALIDATION PASSED ===');
    return true;
  };

  const handlePhoneAuth = async () => {
    console.log('handlePhoneAuth called');
    console.log('isLoading:', isLoading);

    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    console.log('Form validation passed, starting registration...');
    setIsLoading(true);

    try {
      const completePhone = `08${phone}` || "";
      let password = "";

      // get stored password from localStorage for given number
      const storedCredentialString = await AsyncStorage.getItem('zclub-credential');
      if(!!storedCredentialString) {
        const storedCredential = JSON.parse(storedCredentialString);
        if(storedCredential.phone == completePhone) {
          password = storedCredential.password;
        }
      }

      // Prepare registration data according to updated API specification
      const registrationData = {
        'telp': completePhone || "",
        'password': atob(password) || "",
        'captcha-token': captchaToken,
      };

      console.log('Sending registration request...');

      const response = await fetch(`${API_BASE_URL}/api/phone-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(registrationData)
      });

      const result = await response.json();
      // console.log('Registration response:', result);

      if (result.status === 'success' && result.data) {
        // if given phone number already registered and have complete data
        // redirect to login instead
        // exit early
        if (result.data.isExistingUser ?? null) {
          await AsyncStorage.setItem('pendingPhone', result.data.telp ?? "");
          navigation.navigate('Login');
          return;
        }

        showCustomAlert(
          'Berhasil',
          result.message || 'Pendaftaran berhasil!',
          'success'
        );

        if (result.data.userData) {
          await AsyncStorage.setItem('userData', JSON.stringify(result.data.userData));
        }

        // if user has no password set, redirect to create password
        if( !result.data.hasPassword ) {
          setTimeout(() => {
            navigation.navigate('PasswordCreate');
          }, 2000);
        } else {
          // Store session data if provided
          if (result.data.userData?.remember_token) {
            await AsyncStorage.setItem('userToken', result.data.userData?.remember_token);
          }
          // Store session data if provided
          if (result.data.userData?.session_id) {
            await AsyncStorage.setItem('session_id', result.data.userData?.session_id);
          }

          window.location.reload();
        }
      } else {
        showCustomAlert('Error', result.message || 'Pendaftaran gagal. Silakan coba lagi.');
      }

    } catch (error) {
      console.error('Registration error:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showCustomAlert('Error', 'Koneksi internet bermasalah. Silakan periksa koneksi Anda.');
      } else {
        showCustomAlert('Error', error.message || 'Pendaftaran gagal. Silakan coba lagi.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleButtonClicked(true);

    const formData = new FormData();
    formData.append('login-from', "ZCLUB");
    formData.append('src-member', "mobile_app");
    formData.append('captcha-token', captchaToken);

    // allow empty phone
    if( !!phone ) {
      if (!validateForm()) {
        console.log('Form validation failed');
        return;
      }

      const completePhone = `08${phone}` || "";
      formData.append('telp', completePhone);
    }
    
    await fetch(`${API_BASE_URL}/api/auth/google/login`, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'include',
      body: formData,
    })
    .then(response => response.json())
    .then(result => {
      if( !result.success ) {
        throw new Error( result.message || 'Terjadi kesalahan, silakan coba beberapa saat lagi' );
      }

      if( !!result.auth_url && URL.canParse(result.auth_url) ) {
        window.location.replace( result.auth_url );
      }
    })
    .catch(e => {
      console.log( e );
      setGoogleButtonClicked(false);
      showCustomAlert('Error', e.message || 'Terjadi kesalahan, silakan coba beberapa saat lagi');
    });
  };

  const handleFacebookLogin = async () => {
    setFacebookButtonClicked(true);
    
    const formData = new FormData();
    formData.append('login-from', "ZCLUB");
    formData.append('src-member', "mobile_app");
    formData.append('captcha-token', captchaToken);

    // allow empty phone
    if( !!phone ) {
      if (!validateForm()) {
        console.log('Form validation failed');
        return;
      }

      const completePhone = `08${phone}` || "";
      formData.append('telp', completePhone);
    }
    
    await fetch(`${API_BASE_URL}/api/auth/facebook/login`, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'include',
      body: formData,
    })
    .then(response => response.json())
    .then(result => {
      if( !result.success ) {
        throw new Error( result.message || 'Terjadi kesalahan, silakan coba beberapa saat lagi' );
      }

      if( !!result.auth_url && URL.canParse(result.auth_url) ) {
        window.location.replace( result.auth_url );
      }
    })
    .catch(e => {
      console.log( e );
      setFacebookButtonClicked(false);
      showCustomAlert('Error', e.message || 'Terjadi kesalahan, silakan coba beberapa saat lagi');
    });
  };

  const handleAppleLogin = async () => {
    setAppleButtonClicked(true);
    
    const formData = new FormData();
    formData.append('login-from', "ZCLUB");
    formData.append('src-member', "mobile_app");
    formData.append('captcha-token', captchaToken);

    // allow empty phone
    if( !!phone ) {
      if (!validateForm()) {
        console.log('Form validation failed');
        return;
      }

      const completePhone = `08${phone}` || "";
      formData.append('telp', completePhone);
    }
    
    await fetch(`${API_BASE_URL}/api/auth/apple/login`, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'include',
      body: formData,
    })
    .then(response => response.json())
    .then(result => {
      if( !result.success ) {
        throw new Error( result.message || 'Terjadi kesalahan, silakan coba beberapa saat lagi' );
      }

      if( !!result.auth_url && URL.canParse(result.auth_url) ) {
        window.location.replace( result.auth_url );
      }
    })
    .catch(e => {
      console.log( e );
      setAppleButtonClicked(false);
      showCustomAlert('Error', e.message || 'Terjadi kesalahan, silakan coba beberapa saat lagi');
    });
  };

  useFocusEffect(
    React.useCallback(()=>{
      // loadSettings();
      // loadProvinces();

      async function checkAuthErrorMessage() {
        const authErrorMessage = await AsyncStorage.getItem('auth_error_message');
        if( !!authErrorMessage ) {
          showCustomAlert('Error', authErrorMessage || 'Terjadi kesalahan, silakan coba beberapa saat lagi');
          await AsyncStorage.removeItem('auth_error_message');
        }
      }

      checkAuthErrorMessage();
    }, [navigation])
  );

  const [fontsLoaded] = useFonts({
    'Poppins': require('../../assets/fonts/Poppins-Regular.ttf'),
    'Poppins-Medium': require('../../assets/fonts/Poppins-Medium.ttf'),
    'Poppins-SemiBold': require('../../assets/fonts/Poppins-SemiBold.ttf'),
    'Poppins-Bold': require('../../assets/fonts/Poppins-Bold.ttf'),
  });

  // Remove showAuthAlert function as we no longer need authentication restrictions

  if (!fontsLoaded) {
    return (
      <View style={styles.loadingContainer}>
        <Text>Loading...</Text>
      </View>
    );
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <View style={styles.container}>
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.header}>
            <TouchableOpacity
              style={styles.backButton}
              onPress={() => navigation.goBack()}
            >
              <Ionicons name="arrow-back" size={24} color="#000000" />
            </TouchableOpacity>
          </View>

          <View style={styles.content}>
            <View style={styles.titleSection}>
              <Text style={styles.title}>Selamat Datang</Text>
              <Text style={styles.subtitle}>
                Silakan masukan nomor handphone untuk masuk dan jelajahi Zoolyfe Club
              </Text>
            </View>

            <View style={styles.formSection}>

              <View style={styles.formGroup}>
                <Text style={styles.label}>Nomor Handphone</Text>
                <View style={styles.inputContainer}>
                  <Text style={styles.inputPrefix}>08-</Text>
                  <MaskedTextInput
                    mask="999-9999999999"
                    style={styles.input}
                    placeholder="xxx-xxxxxxx"
                    placeholderTextColor="#94A3B8"
                    value={phone}
                    onChangeText={(text, rawText) => {
                      setPhone(rawText);
                    }}
                    keyboardType="phone-pad"
                    autoCorrect={false}
                  />
                </View>
              </View>

              <TouchableOpacity
                style={[styles.registerButton, styles.externalAuthButton, styles.googleButton, (isLoading || googleButtonClicked) && styles.disabledButton]}
                onPress={handleGoogleLogin}
                disabled={isLoading || googleButtonClicked}
              >
                <FontAwesome5 style={styles.externalAuthButtonIcon} name="google" size={14} color="#000000" />
                <Text style={[styles.registerButtonText, styles.googleButtonText]}>Lanjutkan dengan Google</Text>
              </TouchableOpacity>

              {/*<TouchableOpacity
                style={[styles.registerButton, styles.externalAuthButton, styles.facebookButton, (isLoading || facebookButtonClicked) && styles.disabledButton]}
                onPress={handleFacebookLogin}
                disabled={isLoading || facebookButtonClicked}
              >
                <FontAwesome5 style={styles.externalAuthButtonIcon} name="facebook" size={14} color="#FFFFFF" />
                <Text style={styles.registerButtonText}>Lanjutkan dengan Facebook</Text>
              </TouchableOpacity>*/}

              <TouchableOpacity
                style={[styles.registerButton, styles.externalAuthButton, styles.appleButton, (isLoading || appleButtonClicked) && styles.disabledButton]}
                onPress={handleAppleLogin}
                disabled={isLoading || appleButtonClicked}
              >
                <FontAwesome5 style={styles.externalAuthButtonIcon} name="apple" size={14} color="#FFFFFF" />
                <Text style={styles.registerButtonText}>Lanjutkan dengan Apple</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.registerButton, (isLoading || !phone) && styles.disabledButton]}
                onPress={handlePhoneAuth}
                disabled={(isLoading || !phone)}
              >
                <Text style={styles.registerButtonText}>
                  {isLoading ? 'Memproses...' : 'Lanjutkan'}
                </Text>
              </TouchableOpacity>

              <View style={styles.loginContainer}>
                <Text style={styles.loginText}>Sudah punya akun? </Text>
                <TouchableOpacity onPress={() => navigation.navigate('Login')}>
                  <Text style={styles.loginLink}>Masuk</Text>
                </TouchableOpacity>
              </View>

              {bannerWrapper}

              {/* Turnstile renders only on web */}
              {Platform.OS === 'web' && (
                <TurnstileWidget
                  siteKey={Constants.expoConfig?.extra?.TURNSTILE_SITE_KEY}
                  onVerify={(token) => setCaptchaToken(token)}
                />
              )}
            </View>
          </View>
        </ScrollView>
      </View>

      {customAlertModal}
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  scrollContent: {
    flexGrow: 1,
    padding: 16,
    paddingTop: 32,
  },
  header: {
    marginBottom: 32,
  },
  backButton: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
  },
  titleSection: {
    marginBottom: 32,
  },
  title: {
    fontSize: 30,
    lineHeight: 36,
    fontFamily: 'Poppins-Semibold',
    fontWeight: 600,
    color: '#00000',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    lineHeight: 24,
    fontFamily: 'Poppins',
    color: '#64748B',
  },
  formSection: {
    flex: 1,
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontFamily: 'Poppins-Medium',
    fontWeight: 500,
    color: '#1E293B',
    marginBottom: 8,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#AAAAAA',
    paddingHorizontal: 16,
    height: 48,
  },
  inputContainerDisabled: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E2E8F0',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#AAAAAA',
    paddingHorizontal: 16,
    height: 48,
  },
  input: {
    flex: 1,
    fontSize: 16,
    color: '#1E293B',
    fontFamily: 'Poppins',
  },
  inputDisabled: {
    flex: 1,
    fontSize: 16,
    color: '#1E293B',
    backgroundColor: '#E2E8F0',
  },
  inputPrefix: {
    fontSize: 16,
    color: '#1E293B',
    fontFamily: 'Poppins',
    // marginRight: 8,
  },
  eyeIcon: {
    padding: 4,
  },
  inputIcon: {
    marginLeft: 8,
  },
  uploadContainer: {
    borderWidth: 1,
    borderColor: '#94A3B8',
    borderStyle: 'dashed',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    height: 136,
    justifyContent: 'center',
    alignItems: 'center',
  },
  uploadContent: {
    alignItems: 'center',
    width: 208,
  },
  uploadText: {
    fontSize: 14,
    color: '#1E293B',
    marginTop: 8,
    fontWeight: '600',
    fontFamily: 'Poppins',
    textAlign: 'center',
  },
  uploadSubtext: {
    fontSize: 12,
    color: '#94A3B8',
    marginTop: 4,
    fontFamily: 'Poppins',
    textAlign: 'center',
    lineHeight: 16,
  },
  uploadContainerProcessing: {
    borderColor: '#3B82F6',
    backgroundColor: '#F0F9FF',
  },
  ocrHintText: {
    fontSize: 11,
    color: '#3B82F6',
    marginTop: 6,
    fontFamily: 'Poppins',
    textAlign: 'center',
    fontWeight: '500',
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 24,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    marginTop: 2,
  },
  checkboxChecked: {
    backgroundColor: '#000000',
    borderColor: '#000000',
  },
  checkboxText: {
    flex: 1,
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
  },
  linkText: {
    color: '#3B82F6',
    fontWeight: '500',
  },
  registerButton: {
    backgroundColor: '#1E293B',
    borderRadius: 6,
    padding: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  disabledButton: {
    opacity: 0.5,
  },
  registerButtonText: {
    fontFamily: 'Poppins-Medium',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 20,
    color: '#FFFFFF',
  },
  externalAuthButton: {
    flexDirection: 'row',
    gap: 8,
  },
  externalAuthButtonIcon: {
    width: 14, 
    justifyContent: 'center',
  },
  googleButton: {
    backgroundColor: '#FFFFFF',
    borderColor: '#9CA3AF',
    borderWidth: 1,
  },
  googleButtonText: {
    color: '#000000',
  },
  facebookButton: {
    backgroundColor: '#2563eb',
  },
  appleButton: {
    backgroundColor: '#0f172a',
  },
  loginContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loginText: {
    fontSize: 14,
    fontFamily: 'Poppins',
    fontWeight: 400,
    color: '#64748B',
  },
  loginLink: {
    fontSize: 14,
    fontFamily: 'Poppins-Semibold',
    fontWeight: 600,
    color: '#1E293B',
  },
});
